





































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { cloneDeep } from 'lodash';

import { IAccessConditionTargetData, AccessTargetType } from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';
import WorkflowFormTemplateStakeholderUsersAutoCompletePicker from '@/components/workflow-form-builder/WorkflowFormTemplateStakeholderUsersAutoCompletePicker.vue';

export default defineComponent({
  name: 'WorkflowFormBuilderRevisionGrantAccessTargetFormDialog',
  components: {
    ALDialog,
    GroupAutoCompletePicker,
    WorkflowFormTemplateStakeholderUsersAutoCompletePicker
  },

  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    target: {
      type: Object as PropType<IAccessConditionTargetData | null>,
      default: null
    }
  },

  emits: ['update:dialog', 'save'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const targetFormRef = ref<ALForm | null>(null);
    const valid = ref(false);
    const error = ref<ISystemError | null>(null);

    const targetFormData = ref<IAccessConditionTargetData>({});

    const targetTypeOptions = [
      {
        text: 'User',
        value: AccessTargetType.User,
        icon: 'mdi-account'
      },
      {
        text: 'Group',
        value: AccessTargetType.Group,
        icon: 'mdi-account-group'
      }
    ];

    const subtitle = computed(() => {
      return `${props.isEdit ? 'Edit' : 'Add'} access target`;
    });

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        clearErrors();
        if (props.isEdit && props.target) {
          targetFormData.value = cloneDeep(props.target);
        } else {
          targetFormData.value = {} as IAccessConditionTargetData;
        }
      }
    });

    function closeDialog() {
      localDialog.value = false;
      targetFormData.value = {} as IAccessConditionTargetData;
      clearErrors();
      resetFormValidation();
    }

    function onTargetTypeChange() {
      // Clear the other target field when type changes
      if (targetFormData.value.target_type === AccessTargetType.User) {
        targetFormData.value.target_group = null;
      } else if (targetFormData.value.target_type === AccessTargetType.Group) {
        targetFormData.value.target_user = null;
      }
    }

    function clearErrors() {
      error.value = null;
    }

    function resetFormValidation(): void {
      if (targetFormRef.value) {
        return targetFormRef.value.resetValidation();
      }
    }

    function validateTargetForm(): boolean {
      // Validate target_type is selected
      if (!targetFormData.value.target_type) {
        if (targetFormRef.value) {
          targetFormRef.value.validate();
        }
        return false;
      }

      // Validate target_user or target_group is set based on type
      if (
        targetFormData.value.target_type === AccessTargetType.User &&
        !targetFormData.value.target_user
      ) {
        if (targetFormRef.value) {
          targetFormRef.value.validate();
        }
        return false;
      }

      if (
        targetFormData.value.target_type === AccessTargetType.Group &&
        !targetFormData.value.target_group
      ) {
        if (targetFormRef.value) {
          targetFormRef.value.validate();
        }
        return false;
      }

      let valid = false;
      if (targetFormRef.value) {
        valid = targetFormRef.value.validate();
      }
      return valid;
    }

    function saveTarget() {
      clearErrors();
      const isValid = validateTargetForm();

      if (isValid) {
        const targetData: IAccessConditionTargetData = cloneDeep(
          targetFormData.value
        );

        emit('save', targetData);
        closeDialog();
      }
    }

    return {
      localDialog,
      targetFormRef,
      valid,
      error,
      targetFormData,
      targetTypeOptions,
      subtitle,
      AccessTargetType,
      requiredValidator,
      closeDialog,
      onTargetTypeChange,
      saveTarget,
      clearErrors
    };
  }
});
